/**
* Контракт: звітування про недійсну електронну адресу для запрошень на участь у тендері.
*
* Шлюз відповідає на HTTP 200 та зберігає `data`; кожна відхилена адреса є помилкою GraphQL з
* `code: 'EMAIL_REJECTED'` та `extensions: { email, reason, suggestion }`. Попередження надходять у * рівні відповіді `extensions.emailWarnings: [{ email, reason }]` та ніколи не змінюють `data`.
* `reason` – це ім'я SmartEnum у форматі PascalCase (наприклад, `PossibleDomainTypo`, `DomainHasNoMx`).
*/

export const EMAIL_REJECTED_CODE = 'EMAIL_REJECTED'

// SmartEnum names of EmailQuality (EmailRejectReason + EmailWarnReason) that have a locale key
const KNOWN_REASONS = [
  'InvalidSyntax', 'InvalidTld', 'DomainIsPublicSuffix', 'DisposableDomain', 'PossibleDomainTypo',
  'DomainNotFound', 'DomainCannotReceiveMail', 'PlaceholderLocalPart', 'ProviderLocalPartRule',
  'TooShortLocalPart', 'DomainHasNoMx', 'ShortNumericLocalPart'
]

/**
 * @typedef {{ email: string, reason: string, suggestion?: string }} EmailRejection
 * @typedef {{ email: string, reason: string }} EmailWarning
 */

/**
 * @param {Array} graphQLErrors Apollo `res.errors` / `error.graphQLErrors`
 * @returns {EmailRejection[]} один запис на помилку EMAIL_REJECTED; інші помилки ігноруються
 */
export function extractEmailRejections(graphQLErrors) {
  if (!Array.isArray(graphQLErrors)) {
    return []
  }
  return graphQLErrors
    .filter(error => error?.extensions?.code === EMAIL_REJECTED_CODE)
    .map(({ extensions }) => ({
      email: extensions.email,
      reason: extensions.reason,
      suggestion: extensions.suggestion
    }))
}

/**
 * @param {object} extensions response-level `extensions`
 * @returns {EmailWarning[]} `extensions.emailWarnings`, or []
 */
export function extractEmailWarnings(extensions) {
  const warnings = extensions?.emailWarnings
  return Array.isArray(warnings) ? warnings : []
}

/**
 * Локалізований, зрозумілий для людини текст для одного відхилення (пошук за ключем причини із загальним резервним варіантом
 * для невідомих кодів; додає пропозицію, якщо вона є).
 * @param {EmailRejection} rejection
 * @param {Function} t vue-i18n `$t`
 * @returns {string}
 */
export function rejectionMessage(rejection, t) {
  const key = KNOWN_REASONS.includes(rejection?.reason) ? rejection.reason : 'Unknown'
  let message = `${rejection?.email} — ${t(`EmailRejection.${key}`)}`
  if (rejection?.suggestion) {
    message += ` (${t('EmailRejection.DidYouMean')}: ${rejection.suggestion})`
  }
  return message
}

/**
 * @param {string[]} emails
 * @param {EmailRejection[]} rejected
 * @returns {string[]} електронні листи не відхилено (порівняння без урахування регістру)
 */
export function withoutRejected(emails, rejected) {
  const rejectedEmails = new Set((rejected || []).map(item => item.email?.toLowerCase()))
  return (emails || []).filter(email => !rejectedEmails.has(email?.toLowerCase()))
}
